/* ==========================================================================
   Pickld header, M2 menu (2026-10-18). sections/pickld-header-m2.liquid.
   Docs: docs/components/menu-m2.md. No framework, no dependencies.

   What this does:
   - Opens the right menu for the width: the mega panel at 1024px and up (under
     the header, with the page dimmed below it), the tab sheet under 1024px.
     Crossing the breakpoint while open closes it.
   - Moves the panel, the sheet and the backdrop to <body> once, because the
     header group is its own stacking context (z-index 4) and anything left
     inside it is painted under page content such as the sticky filter column.
   - Closes on Escape, on the close button, and on ANY click outside the open
     menu. The outside click is one document-level listener on purpose: a
     backdrop-only handler left the old drawer stuck open on 2026-09-09 when an
     app overlay swallowed the click. Clicks on a trigger are excluded (they
     toggle), clicks inside the menu are excluded (links and tabs work). Also
     closes on pagehide, so a page restored from the back-forward cache is not
     left scroll-locked. With the menu closed, Escape closes the header search
     dropdown.
   - Traps Tab inside the open menu (the desktop cycle includes the Shop button
     above the panel), returns focus to the trigger on close, locks the page
     scroll with a body class (pickld-m2-open, which also hides the app widgets
     and sticky buy bars, see the stylesheet), and marks the closed menu inert so
     nothing inside it can be tabbed to or read out.
   - Tabs: click, or Left/Right/Home/End on a focused tab (automatic activation).
     A [data-m2-goto="<tab>"] button (Top brands' "All N") selects that tab.
   - Sheet search: while Dawn's predictive search shows results, the sheet gets
     is-searching and the results replace the tab bodies. Escape clears the
     search first and closes the sheet on the second press.
   - Cart: the cart icons keep [data-pickld-cart-toggle], the contract both cart
     drawers honour. A cart icon inside the sheet closes the menu in the CAPTURE
     phase first, so the cart drawer records the menu trigger as the place to
     return focus to, not an icon inside a closed sheet.
   - Labels first, counts on demand (2026-10-19, speed audit regressions 1 and 3):
     the section renders labels only on every ordinary page (data-m2-partial="true"),
     because scanning the catalogue on each render cost about 300 ms of server time
     and put all 153 products in the analytics script. Once per page view this
     fetches the complete menu from <root>/cart?sections=<section id> (the cart
     path is the section's switch for "render the scans", see the section's header
     comment; Shopify caches that response for every visitor) and swaps every
     [data-m2-swap] region (panel, sheet-paddles, sheet-brands, sheet-gear) for the
     complete one. When: on the first hover, focus or touch of a menu button, on
     open, and once the page has loaded and the browser is idle (not on Save-Data).
     A failed fetch keeps the labels-only menu and the next intent retries. The
     same fetch completes paginated and filtered pages.
   - Pictures: the closed panel and sheet are display:none (CSS), so their lazy
     pictures are not fetched. The first intent (the same moment as the fetch) adds
     .is-live, which turns the panel on while it is still hidden by opacity, so the
     pictures arrive before the click completes. .is-live is never removed.
   - Theme editor: rebinds on shopify:section:load, cleans up its moved nodes on
     shopify:section:unload, opens the menu while the section is selected and
     closes it on deselect.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
  var instances = {};

  function visible(el) {
    return !!(el.getClientRects().length) && el.tabIndex >= 0 && !el.closest('[hidden],[inert]');
  }

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), visible);
  }

  function focusEl(el) {
    if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
  }

  function Menu(root) {
    this.root = root;
    this.id = root.getAttribute('data-section-id') || '';
    this.panel = root.querySelector('[data-m2-panel]');
    this.sheet = root.querySelector('[data-m2-sheet]');
    this.backdrop = root.querySelector('[data-m2-backdrop]');
    if (!this.panel || !this.sheet || !this.backdrop) return;

    this.triggers = Array.prototype.slice.call(root.querySelectorAll('[data-m2-trigger]'));
    this.shopButton = root.querySelector('.pickld-m2__shop');
    this.burger = root.querySelector('.pickld-m2__burger');
    this.sbody = this.sheet.querySelector('[data-m2-sbody]');
    this.tabs = Array.prototype.slice.call(this.sheet.querySelectorAll('[data-m2-tab]'));
    this.sheetSearch = this.sheet.querySelector('[data-m2-search="sheet"]');
    this.mq = window.matchMedia(DESKTOP);
    this.openEl = null;
    this.lastTrigger = null;
    this.partial = root.getAttribute('data-m2-partial') === 'true';
    this.fetched = false;
    this.ac = typeof AbortController === 'function' ? new AbortController() : null;

    // Move the backdrop, panel and sheet to <body>, backdrop first so it sits
    // under them in document order as well as by z-index.
    var body = document.body;
    [this.backdrop, this.panel, this.sheet].forEach(function (el) {
      if (el.parentNode !== body) body.appendChild(el);
    });

    this.syncControls();
    this.bind();
    this.watchSearch();
    if (this.partial) {
      this.watchIntent();
      this.scheduleFetch();
    }
  }

  Menu.prototype.on = function (target, type, fn, capture, passive) {
    var opts = { capture: !!capture };
    if (passive) opts.passive = true;
    if (this.ac) opts.signal = this.ac.signal;
    target.addEventListener(type, fn, opts);
  };

  Menu.prototype.syncControls = function () {
    // Below 1024px the Shop button (shown from 768px) opens the sheet.
    if (this.shopButton) this.shopButton.setAttribute('aria-controls', this.mq.matches ? this.panel.id : this.sheet.id);
    if (this.burger) this.burger.setAttribute('aria-controls', this.sheet.id);
  };

  Menu.prototype.setExpanded = function (open) {
    this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', open ? 'true' : 'false'); });
  };

  Menu.prototype.open = function (trigger) {
    var desktop = this.mq.matches;
    var el = desktop ? this.panel : this.sheet;
    if (this.openEl === el) return;
    if (this.openEl) this.close(false);
    this.lastTrigger = trigger || null;

    // The panel hangs from the bottom edge of the header as it is on screen now.
    var top = desktop ? Math.max(0, Math.round(this.root.getBoundingClientRect().bottom)) : 0;
    this.panel.style.setProperty('--m2-top', top + 'px');
    this.backdrop.style.setProperty('--m2-top', top + 'px');

    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--m2-sbw', (sbw > 0 ? sbw : 0) + 'px');
    document.body.classList.add('pickld-m2-open');

    el.removeAttribute('inert');
    // display:none until now (or since the first intent): switch it on and let the
    // browser compute the closed state once, so the open transition still plays.
    el.classList.add('is-live');
    void el.offsetWidth;
    el.classList.add('is-open');
    this.backdrop.classList.add('is-open');
    this.openEl = el;
    this.setExpanded(true);
    this.fetchComplete();

    // Desktop: the first link. Phone: the close button, so the keyboard does
    // not jump up over the sheet the moment it opens.
    if (desktop) {
      focusEl(focusables(el)[0]);
    } else {
      focusEl(el.querySelector('[data-m2-close]'));
    }
  };

  Menu.prototype.close = function (restoreFocus) {
    var el = this.openEl;
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('inert', '');
    this.backdrop.classList.remove('is-open');
    document.body.classList.remove('pickld-m2-open');
    document.body.style.removeProperty('--m2-sbw');
    this.openEl = null;
    this.setExpanded(false);
    var trigger = this.lastTrigger;
    this.lastTrigger = null;
    if (restoreFocus !== false && trigger && document.documentElement.contains(trigger)) focusEl(trigger);
  };

  Menu.prototype.selectTab = function (name, moveFocus) {
    var selected = null;
    this.tabs.forEach(function (tab) {
      var on = tab.getAttribute('data-m2-tab') === name;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on) selected = tab;
    });
    if (!selected) return;
    Array.prototype.forEach.call(this.sheet.querySelectorAll('[data-m2-tabpanel]'), function (panel) {
      panel.hidden = panel.getAttribute('data-m2-tabpanel') !== name;
    });
    if (this.sbody) this.sbody.scrollTop = 0;
    if (moveFocus) focusEl(selected);
  };

  Menu.prototype.searchIsActive = function () {
    if (!this.sheetSearch) return false;
    var input = this.sheetSearch.querySelector('input[type="search"]');
    return !!(input && input.value) || this.sheet.classList.contains('is-searching');
  };

  Menu.prototype.clearSearch = function () {
    if (!this.sheetSearch) return;
    var form = this.sheetSearch.querySelector('form');
    var input = this.sheetSearch.querySelector('input[type="search"]');
    var ps = this.sheetSearch.querySelector('predictive-search');
    if (ps && typeof ps.close === 'function') ps.close(true);
    if (input) input.value = '';
    if (form) form.reset();
    this.sheet.classList.remove('is-searching');
    focusEl(input);
  };

  // While Dawn's predictive search is open or loading in the sheet, its results
  // take the place of the tabs.
  Menu.prototype.watchSearch = function () {
    var ps = this.sheetSearch && this.sheetSearch.querySelector('predictive-search');
    if (!ps || typeof MutationObserver !== 'function') return;
    var sheet = this.sheet;
    var update = function () {
      sheet.classList.toggle('is-searching', ps.hasAttribute('open') || ps.hasAttribute('loading'));
    };
    this.observer = new MutationObserver(update);
    this.observer.observe(ps, { attributes: true, attributeFilter: ['open', 'loading'] });
  };

  // The first hover, focus or touch of a menu button: fetch the complete menu and
  // switch the panel on (still hidden by opacity) so its pictures load now, ahead
  // of the click. A mouse only counts as intent when the pointer really is a mouse.
  Menu.prototype.warm = function () {
    if (this.warmed) return;
    this.warmed = true;
    (this.mq.matches ? this.panel : this.sheet).classList.add('is-live');
    this.fetchComplete();
  };

  Menu.prototype.watchIntent = function () {
    var self = this;
    var warm = function () { self.warm(); };
    this.triggers.forEach(function (t) {
      self.on(t, 'pointerenter', function (e) { if (e.pointerType === 'mouse') warm(); }, false, true);
      self.on(t, 'focus', warm, false, true);
      self.on(t, 'touchstart', warm, false, true);
    });
  };

  // After the page has loaded and the browser is idle, so the request never competes
  // with anything the shopper is waiting for. Skipped on Save-Data; intent still works.
  Menu.prototype.scheduleFetch = function () {
    var self = this;
    var conn = navigator.connection;
    if (conn && conn.saveData) return;
    var run = function () { self.fetchComplete(); };
    var idle = function () {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 4000 });
      } else {
        window.setTimeout(run, 2000);
      }
    };
    if (document.readyState === 'complete') idle();
    else this.on(window, 'load', idle);
  };

  // Fetch the complete menu: this section as the cart page renders it (the section
  // prints its scans only there, see the section's header comment) and swap the
  // counted regions in. Once per page view; a failure leaves the labels-only menu in
  // place, which still works, and lets the next intent try again.
  Menu.prototype.fetchComplete = function () {
    if (!this.partial || this.fetched || !this.id || typeof window.fetch !== 'function') return;
    this.fetched = true;
    var self = this;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.fetch(root + 'cart?sections=' + encodeURIComponent(this.id), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('menu ' + r.status); return r.json(); })
      .then(function (json) {
        if (!json || typeof json[self.id] !== 'string') return;
        var doc = new DOMParser().parseFromString(json[self.id], 'text/html');
        var fresh = doc.querySelector('[data-pickld-m2]');
        if (!fresh || fresh.getAttribute('data-m2-partial') === 'true') return;
        [self.panel, self.sheet].forEach(function (container) {
          Array.prototype.forEach.call(container.querySelectorAll('[data-m2-swap]'), function (live) {
            var incoming = doc.querySelector('[data-m2-swap="' + live.getAttribute('data-m2-swap') + '"]');
            if (!incoming) return;
            var hadFocus = live.contains(document.activeElement);
            live.innerHTML = incoming.innerHTML;
            if (hadFocus) focusEl(focusables(live)[0]);
          });
        });
        self.partial = false;
        self.root.setAttribute('data-m2-partial', 'false');
      })
      .catch(function () { self.fetched = false; /* keep the labels-only menu */ });
  };

  Menu.prototype.trap = function (e) {
    var el = this.openEl;
    var list = focusables(el);
    var active = document.activeElement;
    // Desktop: the Shop button above the panel is part of the cycle, but it is
    // not next to the panel in the document (the panel lives at the end of
    // <body>), so every step to or from it is made here, never by the browser.
    var trigger = el === this.panel ? this.lastTrigger : null;
    if (trigger) {
      if (active === trigger) {
        e.preventDefault();
        focusEl(e.shiftKey ? list[list.length - 1] : list[0]);
        return;
      }
      if (list.length && ((e.shiftKey && active === list[0]) || (!e.shiftKey && active === list[list.length - 1]))) {
        e.preventDefault();
        focusEl(trigger);
        return;
      }
    }
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    var inside = el.contains(active);
    if (e.shiftKey && (active === first || !inside)) {
      e.preventDefault();
      focusEl(last);
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault();
      focusEl(first);
    }
  };

  Menu.prototype.onTabKey = function (e, tab) {
    var i = this.tabs.indexOf(tab);
    var n = this.tabs.length;
    var next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % n;
    else if (e.key === 'ArrowLeft') next = (i - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next < 0) return;
    e.preventDefault();
    this.selectTab(this.tabs[next].getAttribute('data-m2-tab'), true);
  };

  Menu.prototype.bind = function () {
    var self = this;

    this.on(document, 'click', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;

      var trig = t.closest('[data-m2-trigger]');
      if (trig && self.root.contains(trig)) {
        e.preventDefault();
        if (self.openEl) self.close(true); else self.open(trig);
        return;
      }
      if (!self.openEl) return;

      if (self.openEl.contains(t)) {
        if (t.closest('[data-m2-close]')) { self.close(true); return; }
        var tab = t.closest('[data-m2-tab]');
        if (tab) { self.selectTab(tab.getAttribute('data-m2-tab'), false); return; }
        // "All 17" beside Top brands: that tab, in place; focus follows for the keyboard.
        var go = t.closest('[data-m2-goto]');
        if (go) { e.preventDefault(); self.selectTab(go.getAttribute('data-m2-goto'), true); return; }
        return; // links inside the menu navigate normally
      }
      self.close(false);
    });

    // A cart icon inside the open menu: close the menu BEFORE the cart drawer's
    // own (bubbling) listener opens the drawer and records where focus returns.
    this.on(document, 'click', function (e) {
      var t = e.target;
      if (!(t instanceof Element) || !self.openEl) return;
      var cart = t.closest('[data-pickld-cart-toggle]');
      if (cart && self.openEl.contains(cart)) self.close(true);
    }, true);

    this.on(document, 'keydown', function (e) {
      if (!self.openEl) {
        // Header search: Escape closes the predictive dropdown (as the old header did).
        if (e.key === 'Escape') {
          var ps = self.root.querySelector('[data-m2-search="header"] predictive-search[open]');
          if (ps && typeof ps.close === 'function') {
            ps.close();
            var input = ps.querySelector('input[type="search"]');
            if (input) input.blur();
          }
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (self.openEl === self.sheet && self.searchIsActive()) { self.clearSearch(); return; }
        self.close(true);
        return;
      }
      if (e.key === 'Tab') { self.trap(e); return; }
      var tab = e.target instanceof Element ? e.target.closest('[data-m2-tab]') : null;
      if (tab && self.sheet.contains(tab)) self.onTabKey(e, tab);
    });

    var onBreakpoint = function () {
      if (self.openEl) self.close(false);
      self.syncControls();
    };
    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', onBreakpoint, this.ac ? { signal: this.ac.signal } : undefined);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(onBreakpoint);
    }

    this.on(window, 'resize', function () {
      if (self.openEl !== self.panel) return;
      var top = Math.max(0, Math.round(self.root.getBoundingClientRect().bottom));
      self.panel.style.setProperty('--m2-top', top + 'px');
      self.backdrop.style.setProperty('--m2-top', top + 'px');
    });

    // Leaving with the menu open would come back from the bfcache scroll-locked.
    this.on(window, 'pagehide', function () { self.close(false); });
  };

  Menu.prototype.destroy = function () {
    this.close(false);
    if (this.ac) this.ac.abort();
    if (this.observer) this.observer.disconnect();
    [this.backdrop, this.panel, this.sheet].forEach(function (el) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    });
  };

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-pickld-m2]'), function (root) {
      if (root.getAttribute('data-m2-bound') === 'true') return;
      root.setAttribute('data-m2-bound', 'true');
      var id = root.getAttribute('data-section-id') || 'm2';
      if (instances[id]) instances[id].destroy();
      var menu = new Menu(root);
      if (menu.panel) instances[id] = menu;
    });
  }

  init(document);

  // Theme editor: a re-rendered section is a new root; its old moved nodes go first.
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && instances[id]) { instances[id].destroy(); delete instances[id]; }
  });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    var menu = e.detail && instances[e.detail.sectionId];
    if (menu) menu.open(null);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var menu = e.detail && instances[e.detail.sectionId];
    if (menu) menu.close(false);
  });

  window.PickldMenu = {
    open: function () { var k = Object.keys(instances)[0]; if (k) instances[k].open(null); },
    close: function () { Object.keys(instances).forEach(function (k) { instances[k].close(false); }); }
  };
})();
